import { useEffect, useState } from "react"
import type { AcceptedWarning, OnboardingValidationIssue, SubmitResult } from "@/api/onboarding-api.types"

export interface WarningAcceptance {
  /** The issues the reviewer may accept. Never includes one that stops the submit. */
  warnings: OnboardingValidationIssue[]
  /** True while a value the destination refuses leaves the reviewer nothing to accept. */
  hasBlockingIssue: boolean
  /** True on the second step, where the list states what proceeding does to each value. */
  isConfirming: boolean
  startConfirming: () => void
  cancelConfirming: () => void
  /** Whether the entries are listed. Always true while confirming, where the detail is the point. */
  isDetailOpen: boolean
  toggleDetail: () => void
  /** What a submit has to carry for these warnings to be allowed through. */
  acceptedWarnings: AcceptedWarning[]
}

/**
 * The two steps a reviewer goes through to proceed past a warning: ask, then be told what proceeding does,
 * then submit.
 *
 * <p>Accepting is deliberately an act of its own. The warnings are values that will be left out or left
 * empty, and a reviewer who clicked an ordinary Proceed would never learn which — that silent discard is
 * what this whole report exists to remove.</p>
 *
 * <p>The confirmation closes whenever the warnings change or a submit comes back, so an acceptance is only
 * ever given for the list the reviewer is actually looking at.</p>
 *
 * <p>The list itself starts closed. It sits in the drawer's footer, which takes its height from the form
 * above it, and a reviewer who already knows what it says should not have to scroll past it to reach the
 * fields they came to change.</p>
 */
export function useWarningAcceptance(
  previewIssues: OnboardingValidationIssue[],
  submitResult: SubmitResult | null,
): WarningAcceptance {
  const [isConfirming, setIsConfirming] = useState(false)
  const [isDetailOpen, setIsDetailOpen] = useState(false)

  const warnings = previewIssues.filter((issue) => !issue.blocking)
  const hasBlockingIssue = previewIssues.some((issue) => issue.blocking)

  // Keyed on the content rather than on the array, which the preflight replaces on every form change: a
  // confirmation left open over a list that no longer exists would accept values nobody was shown.
  const warningKey = warnings.map((issue) => `${issue.field}|${issue.rule}|${issue.consequence}`).join("~")

  useEffect(() => {
    setIsConfirming(false)
    setIsDetailOpen(false)
  }, [warningKey, submitResult])

  return {
    warnings,
    hasBlockingIssue,
    isConfirming: isConfirming && warnings.length > 0,
    startConfirming: () => setIsConfirming(true),
    cancelConfirming: () => setIsConfirming(false),
    isDetailOpen: isDetailOpen || (isConfirming && warnings.length > 0),
    toggleDetail: () => setIsDetailOpen((open) => !open),
    acceptedWarnings: warnings.map((issue) => ({
      field: issue.field,
      rule: issue.rule,
      consequence: issue.consequence,
    })),
  }
}
